/** Manages draft and applied filter states with refresh and clear actions. */
import { useMemo, useState } from 'react';

interface UseAppliedFiltersOptions<T> {
  initialFilters: T;
  areEqual?: (a: T, b: T) => boolean;
}

interface UseAppliedFiltersResult<T> {
  currentFilters: T;
  appliedFilters: T;
  hasChanges: boolean;
  setCurrentFilters: (next: T) => void;
  patchCurrentFilters: (patch: Partial<T>) => void;
  applyFilters: () => void;
  resetAllFilters: (next: T) => void;
}

const defaultAreEqual = <T,>(a: T, b: T): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Returns unified draft/applied filter state primitives for top panel flows. */
export function useAppliedFilters<T>({
  initialFilters,
  areEqual = defaultAreEqual,
}: UseAppliedFiltersOptions<T>): UseAppliedFiltersResult<T> {
  const [currentFilters, setCurrentFiltersState] = useState<T>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<T>(initialFilters);

  const hasChanges = useMemo(
    () => !areEqual(currentFilters, appliedFilters),
    [currentFilters, appliedFilters, areEqual]
  );

  const setCurrentFilters = (next: T) => {
    setCurrentFiltersState(next);
  };

  const patchCurrentFilters = (patch: Partial<T>) => {
    setCurrentFiltersState((prev) => ({ ...prev, ...patch }));
  };

  const applyFilters = () => {
    setAppliedFilters(currentFilters);
  };

  const resetAllFilters = (next: T) => {
    setCurrentFiltersState(next);
    setAppliedFilters(next);
  };

  return {
    currentFilters,
    appliedFilters,
    hasChanges,
    setCurrentFilters,
    patchCurrentFilters,
    applyFilters,
    resetAllFilters,
  };
}

